import { Link } from "react-router-dom";
import { ArrowRight, Heart, Award, Users, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#FFFCF9]" data-testid="about-page">
      {/* Hero */}
      <section className="py-20 bg-spa-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="text-spa-pink-500 font-medium text-sm tracking-widest uppercase">
              Our Story
            </span>
            <h1 className="font-heading text-5xl md:text-6xl text-spa-neutral-800 mt-4 mb-6">
              About Us
            </h1>
            <p className="text-spa-neutral-500 text-lg max-w-2xl mx-auto">
              Welcome to Kriel Colon and Beauty Spa, where beauty meets art.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_1c26490f-4cbf-4297-b6f0-da0519adec07/artifacts/6igciie1_nj.jpeg"
                alt="Professional colon therapy treatment at Kriel Spa"
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/5]"
              />
            </div>
            <div>
              <span className="text-spa-pink-500 font-medium text-sm tracking-widest uppercase">
                Who We Are
              </span>
              <h2 className="font-heading text-4xl text-spa-neutral-800 mt-4 mb-6">
                Your Wellness Destination
              </h2>
              <div className="space-y-4 text-spa-neutral-600 leading-relaxed">
                <p>
                  Treat yourself to a luxurious experience with our expert health and beauty services. At Kriel Colon and Beauty Spa, we believe in providing personalized care that helps you look and feel your absolute best.
                </p>
                <p>
                  Our team of certified professionals brings years of experience in wellness treatments, from colon cleansing and facials to advanced skin treatments. We use only premium quality products to ensure the best results.
                </p>
                <p>
                  We serve clients who are real and know what they want - people who understand the value of professional spa treatments and expect nothing but the best.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-8">
                {[
                  { num: "20+", label: "Services Offered" },
                  { num: "1000+", label: "Happy Clients" },
                  { num: "10+", label: "Years Experience" },
                ].map((stat) => (
                  <div key={stat.label}>
                    <p className="font-heading text-4xl font-semibold text-spa-pink">{stat.num}</p>
                    <p className="text-spa-neutral-500 text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-spa-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-spa-pink-500 font-medium text-sm tracking-widest uppercase">
              Our Values
            </span>
            <h2 className="font-heading text-4xl text-spa-neutral-800 mt-4">
              What We Stand For
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Heart, title: "Relaxing", desc: "Creating a serene environment for complete relaxation" },
              { icon: Award, title: "Professional", desc: "Certified therapists delivering expert treatments" },
              { icon: Users, title: "Respected", desc: "Building trust through exceptional service" },
              { icon: Sparkles, title: "Healthy", desc: "Promoting wellness through natural products" },
            ].map((value, idx) => (
              <div key={idx} className="text-center p-8 rounded-2xl bg-white spa-shadow spa-hover">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-spa-pink-100 rounded-full mb-6">
                  <value.icon className="h-8 w-8 text-spa-pink" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-spa-neutral-800 mb-3">{value.title}</h3>
                <p className="text-spa-neutral-500 text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-spa-pink-500 font-medium text-sm tracking-widest uppercase">
              Trusted Partners
            </span>
            <h2 className="font-heading text-3xl text-spa-neutral-800 mt-4">
              Brands We've Worked With
            </h2>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-12">
            <span className="font-heading text-2xl text-spa-neutral-700">Biomedical Emporium</span>
            <span className="h-12 w-px bg-spa-neutral-200 hidden md:block" />
            <span className="font-heading text-2xl text-spa-neutral-700">Zena Cosmetics</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-spa-pink relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
            Ready to Experience Our Services?
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Book your appointment today and let us help you relax, refresh, and rejuvenate.
          </p>
          <Link to="/booking" data-testid="about-book-now">
            <Button className="bg-white text-spa-pink hover:bg-spa-neutral-100 rounded-lg px-10 py-6 text-lg font-semibold transition-all hover:shadow-lg">
              Book Your Session
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
